<template>
  <PageContainer class="bg-mint" collapse-header>
    <template #header>
      <HomeHero :stats="stats" />
    </template>
    <template #main>
      <HomeAsSeenOn />
      <HomeWhyWait />
      <HomeHowItWorks />
      <HomeResources
        v-if="featureArticle || datedArticles.length"
        :feature-article="featureArticle"
        :articles="datedArticles"
      />
      <HomeSupporters v-if="supporters.length" :supporters="supporters" />
      <HomeFaq />
    </template>
  </PageContainer>
</template>

<script lang="ts" setup>
import type { PaginatedDocs } from 'payload'
import type {
  ResourceArticle,
  ResourceArticleCategory,
  Supporter,
} from '#payload-types'

import {
  GetResourceArticlesByCategoriesDocument,
  GetResourceCategoriesDocument,
  GetSupportersDocument,
} from '#graphql-exports'

interface ResourceArticlesQueryResult {
  ResourceArticles: PaginatedDocs<ResourceArticle>
}

interface ResourceCategoriesQueryResult {
  ResourceArticleCategories: {
    docs: ResourceArticleCategory[]
  }
}

interface SupportersQueryResult {
  Supporters: PaginatedDocs<Supporter>
}

// usePayloadPage('Home') is kept for SEO meta + 404 handling (fields unused).
const [, { stats }, { data: categoryData }, { data: supporterData }] =
  await Promise.all([
    usePayloadPage('Home'),
    useGlobalStats(),
    useAsyncQuery<ResourceCategoriesQueryResult>(GetResourceCategoriesDocument),
    useAsyncQuery<SupportersQueryResult>(GetSupportersDocument, {
      limit: 100,
    }),
  ])

// Payload's GraphQL where inputs cannot reach a relationship's own fields, so
// the two groups are filtered by the ids of the categories on either side of
// the category's Show Date flag.
const categoryIDsShowingDate = (showDate: boolean) =>
  (categoryData.value?.ResourceArticleCategories?.docs ?? [])
    .filter((category) => !!category.showDate === showDate)
    .map((category) => category.id)

const [{ data: featureData }, { data: datedData }] = await Promise.all([
  useAsyncQuery<ResourceArticlesQueryResult>(
    GetResourceArticlesByCategoriesDocument,
    { limit: 1, categoryIDs: categoryIDsShowingDate(false) },
  ),
  useAsyncQuery<ResourceArticlesQueryResult>(
    GetResourceArticlesByCategoriesDocument,
    { limit: 2, categoryIDs: categoryIDsShowingDate(true) },
  ),
])

const featureArticle = computed(
  () => featureData.value?.ResourceArticles.docs[0] ?? null,
)

const datedArticles = computed(
  () => datedData.value?.ResourceArticles.docs ?? [],
)

const supporters = computed(() => supporterData.value?.Supporters.docs ?? [])
</script>
